// composite.filter-bar.v1
//
// Horizontal bar of filter controls + an apply/clear action. Specifically
// designed to replace dense filter forms in plugin analytics +
// CRM records page — where 3-5 selects + a submit button cram into one
// inline row today.
//
// Layout flows:
//   • Each filter is a Field (label above control). Fields wrap on
//     narrow widths via flex-wrap.
//   • Right-aligned action slot for Apply / Clear / Save preset.
//
// Filter widgets themselves are passed in as children — this composite
// owns the spacing, not the inputs.

import { type JSX, Show, splitProps } from "solid-js"

export interface FilterBarFieldProps {
  label: string
  htmlFor?: string
  hint?: string
  children: JSX.Element
}

export function FilterBarField(props: FilterBarFieldProps) {
  return (
    <div data-registry-id="composite.filter-bar.field" class="flex flex-col gap-1 min-w-[140px]">
      <label
        for={props.htmlFor}
        class="text-12-medium uppercase tracking-[0.08em] text-text-weak truncate"
      >
        {props.label}
      </label>
      {props.children}
      <Show when={props.hint}>
        <span class="text-12-regular text-text-weak truncate">{props.hint}</span>
      </Show>
    </div>
  )
}

export interface FilterBarProps {
  children: JSX.Element
  actions?: JSX.Element
  class?: string
}

export function FilterBar(props: FilterBarProps) {
  const [local, rest] = splitProps(props, ["children", "actions", "class"])

  return (
    <div
      data-registry-id="composite.filter-bar"
      classList={{
        "flex flex-wrap items-end gap-3 rounded-xl border border-border-weak-base bg-surface-raised-base p-3":
          true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <div class="flex flex-wrap items-end gap-3 min-w-0 flex-1">{local.children}</div>
      <Show when={local.actions}>
        <div class="flex items-center gap-2 shrink-0">{local.actions}</div>
      </Show>
    </div>
  )
}
